import { useTenantConfigQuery } from './useTenantConfigQuery';
import { getApiUrlOverride } from './getApiUrlOverride';

const DEFAULT_PWA_DISMISS_DAYS = 7;

/**
 * Origin of a tenant env URL (assetUrl/imageUrl). These carry the canonical public
 * host (e.g. https://www.isthmian.co.uk or https://grimsbyboroughfc.com), which can
 * differ from the bare `domain`. Returns '' for missing/unparseable values.
 */
const originFromUrl = (url?: string): string => {
  if (!url) return '';
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
};

export const useTenantEnv = () => {
  const { data: tenantConfig, isLoading } = useTenantConfigQuery();

  return {
    isLoading,
    isReady: !!tenantConfig?.env && !isLoading,
    pwaDismissDays: tenantConfig?.env?.pwaDismissDays ?? DEFAULT_PWA_DISMISS_DAYS,
    isLeagueSite: tenantConfig?.env?.siteType === 'league',
    // Prefer build-time VITE_API_URL so PR sandbox admin calls the PR API, not api-test
    apiUrl: getApiUrlOverride() ?? tenantConfig?.env?.apiUrl ?? '',
    assetUrl: tenantConfig?.env?.assetUrl ?? '',
    imageUrl: tenantConfig?.env?.imageUrl ?? '',
    googleMapsApiKey: tenantConfig?.env?.googleMapsApiKey ?? '',
    siteName: tenantConfig?.env?.siteName ?? '',
    siteType: tenantConfig?.env?.siteType ?? 'football',
    entityName: tenantConfig?.env?.entityName ?? '',
    twitterSite: tenantConfig?.env?.twitterSite ?? '',
    recaptchaSiteKey: tenantConfig?.env?.recaptchaSiteKey,
    // Public website origin for shareable links. Prefer the canonical host carried by
    // assetUrl/imageUrl (can differ from the bare `domain`, e.g. grimsbyboroughfc.com);
    // fall back to the domain. Mirrors the API's resolveSiteBaseUrl precedence.
    siteUrl:
      originFromUrl(tenantConfig?.env?.assetUrl) ||
      originFromUrl(tenantConfig?.env?.imageUrl) ||
      (tenantConfig?.domain ? `https://${tenantConfig.domain}` : ''),
  };
};
